import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button, StudyString, Text, useDateTimeFormat, useLocalizeMessage } from 'libs.nucleus';
import { DetailedResponseValueModal } from './detailed_response_value_modal.component';
import { stepResponseTypes } from '../../constants/step_response_type.constants';
import { escapeNonBreakingSpaces } from '../../helpers/ecoa.format.helpers';
import { getResponseValueForTLFBStep, getStepResponseValueForSearchableVRSStep, } from '../../helpers/step_response.helpers';
import { isEventListData, renderEventListDisplay, processStepResponseWithEventList, resolveEventListLabels, } from '../../helpers/step_response_variable.helpers';
export const StepResponseValue = ({ stepResponse, variables }) => {
    const translate = useLocalizeMessage();
    const { formatDateTime } = useDateTimeFormat();
    const [isDetailedResponseModalOpen, setIsDetailedResponseModalOpen] = useState(false);
    const handleOpenDetailedResponseModal = () => {
        setIsDetailedResponseModalOpen(true);
    };
    const handleCloseDetailedResponseModal = () => {
        setIsDetailedResponseModalOpen(false);
    };
    if (stepResponse?.c_skipped) {
        return _jsx(Text, { children: translate('generic.skipped') });
    }
    if (stepResponse.type === stepResponseTypes?.cText.objectType) {
        const stepScreenDetails = stepResponse.c_step?.c_screen_details;
        const isTLFBScreen = stepScreenDetails?.c_screen_type === 'tlfb';
        if (isTLFBScreen) {
            const responseValue = getResponseValueForTLFBStep(stepResponse);
            const entries = Object.entries(responseValue);
            const nFilledEntries = Object.entries(responseValue).filter((entry) => typeof entry[1] === 'number').length;
            return (_jsxs("div", { className: 'flex flex-row flex-wrap items-center gap-4', children: [_jsx(Text, { children: translate('info.visit_activity_responses.tlfb_response_details', {
                            count: nFilledEntries,
                            total: entries.length,
                        }) }), _jsx(Button, { variant: 'transparent', size: 'xs', label: translate('generic.view'), onClick: handleOpenDetailedResponseModal, dataTestId: 'step-response-tlfb-open-detail' }), _jsx(DetailedResponseValueModal, { isOpen: isDetailedResponseModalOpen, onClose: handleCloseDetailedResponseModal, stepResponse: stepResponse, title: stepResponse.c_step?.c_name })] }));
        }
    }
    //  For image captures where an image is uploaded
    if (stepResponse.type === stepResponseTypes?.cImageCapture.objectType) {
        return (_jsx("img", { alt: stepResponse.c_step.c_name ?? 'Image', "data-testid": `step-response-${stepResponse._id}`, src: stepResponse?.url }));
    }
    //  For Date time formatting
    if (stepResponse.type === stepResponseTypes?.cDatetime.objectType) {
        return _jsx(Text, { children: formatDateTime(stepResponse.c_value) });
    }
    //  Date formatting
    if (stepResponse.type === stepResponseTypes?.cDate.objectType) {
        return _jsx(Text, { children: formatDateTime(stepResponse.c_value, { dateOnly: true }) });
    }
    //  Text choice formatting (with dynamic c_text_choices from event list variables)
    if (stepResponse.type === stepResponseTypes?.cTextChoice.objectType) {
        if (variables && Array.isArray(stepResponse.c_value)) {
            const resolvedLabels = resolveEventListLabels(stepResponse.c_value, variables);
            if (resolvedLabels) {
                return (_jsx(Text, { children: _jsx(StudyString, { children: resolvedLabels }) }));
            }
        }
        // Process step response only when needed for text choices
        const processedStepResponse = variables ? processStepResponseWithEventList(stepResponse, variables) : stepResponse;
        const textChoices = (processedStepResponse?.c_step?.c_text_choices || []).filter((textChoice) => {
            if (Array.isArray(processedStepResponse.c_value)) {
                return processedStepResponse.c_value.includes(textChoice.c_value);
            }
            return textChoice.c_value?.toString() === processedStepResponse.c_value?.toString();
        });
        if (textChoices.length === 0) {
            const searchableOtherDisplay = getStepResponseValueForSearchableVRSStep(processedStepResponse);
            if (searchableOtherDisplay) {
                return (_jsx(Text, { children: _jsx(StudyString, { children: searchableOtherDisplay }) }));
            }
        }
        return (_jsx(Text, { children: _jsx(StudyString, { children: textChoices.map((textChoice) => escapeNonBreakingSpaces(textChoice.c_text)).join(', ') }) }));
    }
    //  Event list variable formatting
    if (isEventListData(stepResponse.c_value)) {
        try {
            const displayText = renderEventListDisplay(stepResponse.c_value);
            if (displayText) {
                return (_jsx("div", { style: { whiteSpace: 'pre-line' }, children: _jsx(Text, { children: _jsx(StudyString, { children: displayText }) }) }));
            }
        }
        catch (error) {
            console.warn('Failed to process event list variable:', error);
        }
    }
    // StudyString only accepts string children; numeric/boolean c_value would otherwise render blank.
    const displayValue = stepResponse.c_value === null || stepResponse.c_value === undefined ? '' : String(stepResponse.c_value);
    return (_jsx(Text, { children: _jsx(StudyString, { children: displayValue }) }));
};
export default StepResponseValue;
